import React, { useState, useEffect, useMemo } from "react";
import { useStepperContext } from "../../context/MainContext";
import {
  communityAmenities,
  flooringType,
  roof,
  streetSurface,
  countertops,
  foundationType,
  energyFeatures,
  laundry,
  garageCarport,
  specialFeatures,
  mainSuite,
  exteriorFeatures,
  hvac,
  waterHeater,
  waterSewer,
  poolDetails,
  exteriorSiding,
  interiorFeatures,
  windowCoverings,
  fenceType,
  locationFeatures,
  conveyingAppliances,
  docsOnFile,
  other,
} from "../../constants/StepsConstants";
import Select from "react-select";

const Features = ({
  propertyData: propPropertyData,
  setPropertyData: propSetPropertyData,
}) => {
  const stepper = useStepperContext();
  const propertyData = useMemo(
    () => propPropertyData || stepper?.propertyData || {},
    [propPropertyData, stepper]
  );
  const setPropertyData =
    propSetPropertyData || (stepper && stepper.setPropertyData);
  const categories = useMemo(
    () => ({
      communityAmenities,
      flooringType,
      roof,
      streetSurface,
      countertops,
      foundationType,
      energyFeatures,
      laundry,
      garageCarport,
      specialFeatures,
      mainSuite,
      exteriorFeatures,
      hvac,
      waterHeater,
      waterSewer,
      poolDetails,
      exteriorSiding,
      interiorFeatures,
      windowCoverings,
      fenceType,
      locationFeatures,
      conveyingAppliances,
      docsOnFile,
      other,
    }),
    []
  );
  const [computed, setComputed] = useState({});
  const [petAllowed, setPetAllowed] = useState(false);
  const [petTypes, setPetTypes] = useState([]); // array of ids as strings
  const [petNonRefundable, setPetNonRefundable] = useState(false);

  useEffect(() => {
    const safePropertyData =
      propertyData && typeof propertyData === "object" ? propertyData : {};
    const byCategory = {};
    // List of all categories

    const featureList = (safePropertyData.feature_list || "")
      .split(",")
      .map((s) => s && s.trim())
      .filter(Boolean);
    const featureSet = new Set(featureList);

    Object.entries(categories).forEach(([catName, features]) => {
      const selected = features
        .filter(
          (f) =>
            featureSet.has(String(f[0])) || safePropertyData[f[0]] === "Yes"
        )
        .map((f) => ({ value: f[0], label: f[1] }));
      byCategory[catName] = selected;
    });
    // initialize pet-related UI state from feature_list
    setPetAllowed(featureList.includes("465"));
    setPetTypes(
      featureList.filter((id) =>
        ["471", "472", "473", "474", "475"].includes(id)
      )
    );
    setPetNonRefundable(featureList.includes("476"));
    setComputed(byCategory);
  }, [propertyData, categories]);
  // apply changes helper: compute updates and write feature_list + flags to propertyData
  const applyFeatureChanges = (
    selectedFeatureIds,
    petAllowedFlag,
    petTypeIds,
    petNonRefund
  ) => {
    const updates = {};
    const categories = {
      communityAmenities,
      flooringType,
      roof,
      streetSurface,
      countertops,
      foundationType,
      energyFeatures,
      laundry,
      garageCarport,
      specialFeatures,
      mainSuite,
      exteriorFeatures,
      hvac,
      waterHeater,
      waterSewer,
      poolDetails,
      exteriorSiding,
      interiorFeatures,
      windowCoverings,
      fenceType,
      locationFeatures,
      conveyingAppliances,
      docsOnFile,
      other,
    };
    // set all to No initially
    Object.values(categories).forEach((arr) =>
      arr.forEach((f) => (updates[f[0]] = "No"))
    );
    // set Yes for selected ids
    (selectedFeatureIds || []).forEach((id) => (updates[id] = "Yes"));
    // pet flags
    if (petAllowedFlag) {
      updates["465"] = "Yes";
      ["471", "472", "473", "474", "475"].forEach((id) => {
        updates[id] = (petTypeIds || []).includes(id) ? "Yes" : "No";
      });
      updates["476"] = petNonRefund ? "Yes" : "No";
      if (!selectedFeatureIds.includes("465")) selectedFeatureIds.push("465");
      (petTypeIds || []).forEach((id) => {
        if (!selectedFeatureIds.includes(id)) selectedFeatureIds.push(id);
      });
      if (petNonRefund && !selectedFeatureIds.includes("476"))
        selectedFeatureIds.push("476");
    } else {
      ["465", "471", "472", "473", "474", "475", "476"].forEach((id) => {
        updates[id] = "No";
        const idx = selectedFeatureIds.indexOf(id);
        if (idx !== -1) selectedFeatureIds.splice(idx, 1);
      });
    }

    const newFeatureList = (selectedFeatureIds || []).join(",");
    if (typeof setPropertyData === "function") {
      setPropertyData((prev) => ({
        ...prev,
        ...updates,
        feature_list: newFeatureList,
      }));
    }
  };

  const handleCategoryChange = (catName, selected) => {
    const selectedIds = (selected || []).map((s) => s.value);
    const currentFeatureList = (propertyData.feature_list || "")
      .split(",")
      .map((s) => s && s.trim())
      .filter(Boolean);
    const categoryIds = (categories[catName] || []).map((f) => String(f[0]));
    const newSelected = currentFeatureList.filter(
      (id) => !categoryIds.includes(String(id))
    );
    selectedIds.forEach((id) => {
      if (!newSelected.includes(id)) newSelected.push(id);
    });
    const petAllowedCur =
      (propertyData.feature_list || "").split(",").includes("465") ||
      propertyData["465"] === "Yes" ||
      propertyData["465"] === true;
    const petTypesCur = (propertyData.feature_list || "")
      .split(",")
      .map((s) => s && s.trim())
      .filter(Boolean)
      .filter((id) => ["471", "472", "473", "474", "475"].includes(id));
    const petNonRefundCur =
      (propertyData.feature_list || "").split(",").includes("476") ||
      propertyData["476"] === "Yes" ||
      propertyData["476"] === true;
    applyFeatureChanges(
      newSelected,
      petAllowedCur,
      petTypesCur,
      petNonRefundCur
    );
  };

  // Handlers for pet UI
  const handlePetTypeChange = (selected) => {
    const ids = (selected || []).map((s) => s.value);
    setPetTypes(ids);
    const currentFeatureList = (propertyData.feature_list || "")
      .split(",")
      .map((s) => s && s.trim())
      .filter(Boolean);
    // remove existing pet type ids then add new ones
    const newSelected = currentFeatureList.filter(
      (id) => !["471", "472", "473", "474", "475"].includes(id)
    );
    ids.forEach((id) => {
      if (!newSelected.includes(id)) newSelected.push(id);
    });
    if (petAllowed && !newSelected.includes("465")) newSelected.push("465");
    if (petNonRefundable && !newSelected.includes("476"))
      newSelected.push("476");
    applyFeatureChanges(newSelected, petAllowed, ids, petNonRefundable);
  };

  const handlePetAllowedChange = (checked) => {
    setPetAllowed(checked);
    const currentFeatureList = (propertyData.feature_list || "")
      .split(",")
      .map((s) => s && s.trim())
      .filter(Boolean);
    if (checked) {
      const newSelected = [
        ...new Set(
          [].concat(
            currentFeatureList,
            ["465"],
            petTypes || [],
            petNonRefundable ? ["476"] : []
          )
        ),
      ];
      applyFeatureChanges(newSelected, true, petTypes, petNonRefundable);
    } else {
      const newSelected = currentFeatureList.filter(
        (id) => !["465", "471", "472", "473", "474", "475", "476"].includes(id)
      );
      applyFeatureChanges(newSelected, false, [], false);
    }
  };

  const handlePetNonRefundableChange = (checked) => {
    setPetNonRefundable(checked);
    const currentFeatureList = (propertyData.feature_list || "")
      .split(",")
      .map((s) => s && s.trim())
      .filter(Boolean);
    const newSelected = checked
      ? [...new Set([].concat(currentFeatureList, ["476"]))]
      : currentFeatureList.filter((id) => id !== "476");
    applyFeatureChanges(newSelected, petAllowed, petTypes, checked);
  };

  const petTypeOptions = [
    { value: "471", label: "🐕 Dog" },
    { value: "472", label: "🐈 Cat" },
    { value: "473", label: "🐠 Fish" },
    { value: "474", label: "🐦 Bird" },
    { value: "475", label: "🦄 Other" },
  ];

  return (
    <div className="flex flex-col">
      <h1 className="font-bold text-30">Features</h1>
      <p className="text-green-600 text-center font-semibold mt-2 mb-4">
        This Information Is Optional & Can Be Edited / Added Later
      </p>
      <p className="text-green-600 text-center font-medium mb-4">
        Filling In This Optional Data Will Award You More Keys
      </p>
      <div className="bg-white border rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Community Amenities */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Community Amenities
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={communityAmenities.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.communityAmenities || []}
              onChange={(selected) =>
                handleCategoryChange("communityAmenities", selected)
              }
              placeholder="Select community amenities..."
              className="w-full"
            />
          </div>

          {/* Flooring Type */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Flooring Type
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={flooringType.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.flooringType || []}
              onChange={(selected) =>
                handleCategoryChange("flooringType", selected)
              }
              placeholder="Select flooring types..."
              className="w-full"
            />
          </div>

          {/* Roof */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Roof</h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={roof.map((a) => ({ value: a[0], label: a[1] }))}
              value={computed.roof || []}
              onChange={(selected) => handleCategoryChange("roof", selected)}
              placeholder="Select roof types..."
              className="w-full"
            />
          </div>

          {/* Street Surface */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Street Surface
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={streetSurface.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.streetSurface || []}
              onChange={(selected) =>
                handleCategoryChange("streetSurface", selected)
              }
              placeholder="Select street surfaces..."
              className="w-full"
            />
          </div>

          {/* Countertops */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Countertops
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={countertops.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.countertops || []}
              onChange={(selected) =>
                handleCategoryChange("countertops", selected)
              }
              placeholder="Select countertops..."
              className="w-full"
            />
          </div>

          {/* Foundation Type */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Foundation Type
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={foundationType.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.foundationType || []}
              onChange={(selected) =>
                handleCategoryChange("foundationType", selected)
              }
              placeholder="Select foundation types..."
              className="w-full"
            />
          </div>

          {/* Energy Features */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Energy Features
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={energyFeatures.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.energyFeatures || []}
              onChange={(selected) =>
                handleCategoryChange("energyFeatures", selected)
              }
              placeholder="Select energy features..."
              className="w-full"
            />
          </div>

          {/* Laundry */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Laundry
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={laundry.map((a) => ({ value: a[0], label: a[1] }))}
              value={computed.laundry || []}
              onChange={(selected) => handleCategoryChange("laundry", selected)}
              placeholder="Select laundry options..."
              className="w-full"
            />
          </div>

          {/* Garage/Carport */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Garage/Carport
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={garageCarport.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.garageCarport || []}
              onChange={(selected) =>
                handleCategoryChange("garageCarport", selected)
              }
              placeholder="Select garage/carport options..."
              className="w-full"
            />
          </div>

          {/* Special Features */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Special Features
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={specialFeatures.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.specialFeatures || []}
              onChange={(selected) =>
                handleCategoryChange("specialFeatures", selected)
              }
              placeholder="Select special features..."
              className="w-full"
            />
          </div>

          {/* Main Suite */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Main Suite
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={mainSuite.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.mainSuite || []}
              onChange={(selected) =>
                handleCategoryChange("mainSuite", selected)
              }
              placeholder="Select main suite features..."
              className="w-full"
            />
          </div>

          {/* Exterior Features */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Exterior Features
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={exteriorFeatures.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.exteriorFeatures || []}
              onChange={(selected) =>
                handleCategoryChange("exteriorFeatures", selected)
              }
              placeholder="Select exterior features..."
              className="w-full"
            />
          </div>

          {/* HVAC */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">HVAC</h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={hvac.map((a) => ({ value: a[0], label: a[1] }))}
              value={computed.hvac || []}
              onChange={(selected) => handleCategoryChange("hvac", selected)}
              placeholder="Select HVAC options..."
              className="w-full"
            />
          </div>

          {/* Water Heater */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Water Heater
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={waterHeater.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.waterHeater || []}
              onChange={(selected) =>
                handleCategoryChange("waterHeater", selected)
              }
              placeholder="Select water heater options..."
              className="w-full"
            />
          </div>

          {/* Water/Sewer */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Water/Sewer
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={waterSewer.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.waterSewer || []}
              onChange={(selected) =>
                handleCategoryChange("waterSewer", selected)
              }
              placeholder="Select water/sewer options..."
              className="w-full"
            />
          </div>

          {/* Pool Details */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Pool Details
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={poolDetails.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.poolDetails || []}
              onChange={(selected) =>
                handleCategoryChange("poolDetails", selected)
              }
              placeholder="Select pool details..."
              className="w-full"
            />
          </div>

          {/* Exterior Siding */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Exterior Siding
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={exteriorSiding.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.exteriorSiding || []}
              onChange={(selected) =>
                handleCategoryChange("exteriorSiding", selected)
              }
              placeholder="Select exterior siding..."
              className="w-full"
            />
          </div>

          {/* Interior Features */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Interior Features
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={interiorFeatures.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.interiorFeatures || []}
              onChange={(selected) =>
                handleCategoryChange("interiorFeatures", selected)
              }
              placeholder="Select interior features..."
              className="w-full"
            />
          </div>

          {/* Window Coverings */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Window Coverings
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={windowCoverings.map((a) => ({
                value: a[0],
                label: a[1],
              }))}
              value={computed.windowCoverings || []}
              onChange={(selected) =>
                handleCategoryChange("windowCoverings", selected)
              }
              placeholder="Select window coverings..."
              className="w-full"
            />
          </div>

          {/* Fence Type */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Fence Type
            </h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={fenceType.map((a) => ({ value: a[0], label: a[1] }))}
              value={computed.fenceType || []}
              onChange={(selected) =>
                handleCategoryChange("fenceType", selected)
              }
              placeholder="Select fence types..."
              className="w-full"
            />
          </div>

          {/* Other */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Other</h3>
            <Select
              closeMenuOnSelect={false}
              isMulti
              options={other.map((a) => ({ value: a[0], label: a[1] }))}
              value={computed.other || []}
              onChange={(selected) => handleCategoryChange("other", selected)}
              placeholder="Select other features..."
              className="w-full"
            />
            {/* Pets UI: separate from Other options */}
            <div className="mt-3 border rounded p-3 bg-gray-50 flex flex-col items-center">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={petAllowed}
                  onChange={(e) =>
                    handlePetAllowedChange(Boolean(e.target.checked))
                  }
                />
                <span className="font-medium">Pets Allowed</span>
              </label>
              {petAllowed && (
                <div className="mt-3 flex flex-col gap-3 items-center w-full">
                  <div className="flex flex-col items-center w-full">
                    <label className="font-medium mb-2">Select Pet Types</label>
                    <div className="w-64">
                      <Select
                        isMulti
                        isSearchable={false}
                        options={petTypeOptions}
                        value={petTypeOptions.filter((o) =>
                          petTypes.includes(o.value)
                        )}
                        onChange={handlePetTypeChange}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-4 justify-center w-full">
                    <div className="flex flex-col items-center">
                      <label className="font-medium">Pet Deposit</label>
                      <input
                        className="bg-slate-300 px-2 w-[120px]"
                        name="pet_deposit"
                        type="number"
                        placeholder="0.00"
                        value={propertyData["pet_deposit"] || ""}
                        onChange={(e) =>
                          setPropertyData((prev) => ({
                            ...prev,
                            pet_deposit: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={petNonRefundable}
                        onChange={(e) =>
                          handlePetNonRefundableChange(
                            Boolean(e.target.checked)
                          )
                        }
                      />
                      <label>Non-Refundable</label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;
